import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Tag, Theme, ToastNotification } from '@carbon/react';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Notebook,
  TrashCan,
  User,
} from '@carbon/icons-react';
import axiosInstance from '../../utils/axiosInstance';
import Collapsible from '../Collapsible';
import authService from '../../services/authService';
import { useTheme } from '../../contexts/ThemeContext';
import {
  NOTES_PAGE_SIZE,
  formatDateTime,
  normalizePagination,
  pluralForm,
  severityLabel,
  severityTagType,
} from '../workday/workDayUtils';
import './AreaNotesPanel.css';

// Normalizuje odgovor GET /api/ShiftNote/area-today/{areaId} ({ success, data: [...] })
// u listu beleški (server ih već vraća od najnovije ka najstarijoj).
const normalizeNotes = (body) => {
  const payload = body && body.data !== undefined ? body.data : body;
  const list = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.notes)
    ? payload.notes
    : [];
  return list
    .filter((note) => note && (note.id || note.note))
    .slice()
    .sort(
      (a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
    );
};

// Beleške za "Trenutno u smeni" (admin) se učitavaju po OBLASTI za tekući dan:
// GET /api/ShiftNote/area-today/{areaId}?pageNumber=&pageSize= (server-side paginacija).

/**
 * AreaNotesPanel - "Beleške smene" za jednu oblast na admin dashboard-u.
 *
 * Prikazuje se ispod liste zaposlenih u okviru panela "Trenutno u smeni".
 * Beleške se učitavaju po OBLASTI za tekući dan
 * (GET /api/ShiftNote/area-today/{areaId}?pageNumber=&pageSize=5) - server-side
 * paginacija, 5 beleški po strani. Prikaz je moguće sklopiti/razviti.
 *
 * Komponenta je prvenstveno za pregled (dodavanje beleški radi zaposleni koji je
 * u smeni, na svom dashboard-u - "Moj radni dan"). Brisanje je dozvoljeno autoru
 * beleške i administratoru (DELETE /api/ShiftNote/{id}), uz potvrdu u modalu.
 */
const AreaNotesPanel = ({ areaId = null, defaultCollapsed = false, onOpenResident = null }) => {
  const { theme } = useTheme();

  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [pagination, setPagination] = useState(() => normalizePagination(null));
  const [toast, setToast] = useState(null);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [actionError, setActionError] = useState(null);

  // Ko sme da obriše belešku: njen autor ili administrator
  // (endpoint: DELETE /api/ShiftNote/{id}).
  const { currentEmployeeId, isAdmin } = useMemo(() => {
    const currentUser = authService.getCurrentUser();
    return {
      currentEmployeeId: currentUser?.employee_id || null,
      isAdmin: authService.getUserRole(currentUser) === 'administrator',
    };
  }, []);

  const showToast = useCallback((kind, title, subtitle) => {
    setToast({ kind, title, subtitle });
    window.setTimeout(() => setToast(null), 4000);
  }, []);

  const fetchNotes = useCallback(
    async (targetPage = 1) => {
      if (!areaId) return;
      setLoading(true);
      setLoadError(null);
      try {
        const response = await axiosInstance.get(`/api/ShiftNote/area-today/${areaId}`, {
          params: { pageNumber: targetPage, pageSize: NOTES_PAGE_SIZE },
        });
        const list = normalizeNotes(response.data);
        setNotes(list);
        setPagination(normalizePagination(response.data, list.length, NOTES_PAGE_SIZE));
      } catch (err) {
        console.error('Greška pri učitavanju beleški smene:', err);
        setNotes([]);
        setPagination(normalizePagination(null, 0, NOTES_PAGE_SIZE));
        setLoadError('Grešku pri učitavanju beleški smene.');
      } finally {
        setLoading(false);
      }
    },
    [areaId]
  );

  useEffect(() => {
    if (!areaId) {
      setNotes([]);
      setLoadError(null);
      setPagination(normalizePagination(null, 0, NOTES_PAGE_SIZE));
      return undefined;
    }
    fetchNotes(1);
    return undefined;
  }, [areaId, fetchNotes]);

  const handlePageChange = useCallback(
    (nextPage) => {
      if (nextPage < 1 || nextPage === pagination.pageNumber) return;
      fetchNotes(nextPage);
    },
    [fetchNotes, pagination.pageNumber]
  );

  // Nema oblasti - nema ni beleški
  if (!areaId) return null;

  // Belešku može obrisati njen autor ili administrator
  const canDeleteNote = (note) =>
    Boolean(
      note?.id &&
        (isAdmin ||
          (currentEmployeeId &&
            note.employeeId &&
            String(note.employeeId).toLowerCase() === String(currentEmployeeId).toLowerCase()))
    );

  const handleDeleteNote = async (note) => {
    if (!note?.id || isDeleting) return;
    setIsDeleting(true);
    setActionError(null);
    try {
      await axiosInstance.delete(`/api/ShiftNote/${note.id}`);
      // Ako je obrisana poslednja beleška na strani, vrati se na prethodnu stranu
      const remaining = Math.max(0, (pagination.totalCount || notes.length) - 1);
      const lastPage = Math.max(1, Math.ceil(remaining / NOTES_PAGE_SIZE));
      const targetPage = Math.min(pagination.pageNumber || 1, lastPage);

      showToast('success', 'Uspešno obrisano', 'Beleška smene je uspešno obrisana.');
      await fetchNotes(targetPage);
    } catch (err) {
      console.error('Greška pri brisanju beleške smene:', err);
      const message =
        err?.response?.data?.message || 'Belešku nije moguće obrisati. Pokušajte ponovo.';
      setActionError(message);
      showToast('error', 'Greška', message);
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
      setNoteToDelete(null);
    }
  };

  // Otvori modal za potvrdu brisanja (isto kao za ostale entitete)
  const openDeleteModal = (note) => {
    setNoteToDelete(note);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setNoteToDelete(null);
  };

  // Broj beleški u zaglavlju = UKUPAN broj (iz paginacije), a ne samo broj
  // beleški na trenutnoj strani.
  const totalCount = pagination.totalCount ?? notes.length;
  const countLabel =
    totalCount === 1
      ? '1 beleška'
      : `${totalCount} ${pluralForm(totalCount, 'beleška', 'beleške', 'beleški')}`;

  return (
    <div className="adash-areanotes">
      <button
        type="button"
        className="adash-areanotes__toggle"
        onClick={() => setIsCollapsed((prev) => !prev)}
        aria-expanded={!isCollapsed}
      >
        <Notebook size={16} className="adash-areanotes__icon" />
        <span className="adash-areanotes__title">Beleške smene</span>
        <span className="adash-areanotes__count">{countLabel}</span>
        <ChevronDown
          size={16}
          className={`adash-areanotes__chevron ${isCollapsed ? '' : 'is-expanded'}`}
        />
      </button>

      <Collapsible open={!isCollapsed}>
        <div className="adash-areanotes__body">
          {actionError && <p className="adash-areanote__error">{actionError}</p>}

          {loading ? (
            <div className="adash-areanote__skeleton">
              <span />
              <span />
            </div>
          ) : loadError ? (
            <p className="adash-areanote__error">{loadError}</p>
          ) : notes.length === 0 ? (
            <p className="adash-areanote__empty">Nema beleški za ovu smenu.</p>
          ) : (
            <ul className="adash-areanote__list">
              {notes.map((note) => {
                const sevLabel = severityLabel(note.severity);
                return (
                  <li key={note.id} className="adash-areanote__item">
                    <div className="adash-areanote__meta">
                      <span className="adash-areanote__author">
                        {note.employeeName || 'Nepoznat autor'}
                      </span>
                      <span className="adash-areanote__time">{formatDateTime(note.createdAt)}</span>
                    </div>

                    {(sevLabel || note.residentName || canDeleteNote(note)) && (
                      <div className="adash-areanote__tags">
                        {sevLabel && (
                          <Tag size="sm" type={severityTagType(note.severity)}>
                            {sevLabel}
                          </Tag>
                        )}

                        {note.residentName &&
                          (onOpenResident && note.residentId ? (
                            <button
                              type="button"
                              className="adash-areanote__resident"
                              onClick={() => onOpenResident(note.residentId)}
                              title={`Otvori profil: ${note.residentName}`}
                            >
                              <User size={12} />
                              {note.residentName}
                            </button>
                          ) : (
                            <span className="adash-areanote__resident">
                              <User size={12} />
                              {note.residentName}
                            </span>
                          ))}

                        {canDeleteNote(note) && (
                          <button
                            type="button"
                            className="adash-areanote__delete"
                            onClick={() => openDeleteModal(note)}
                            aria-label="Obriši belešku"
                            title="Obriši belešku"
                          >
                            <TrashCan size={14} />
                          </button>
                        )}
                      </div>
                    )}

                    <p className="adash-areanote__text">{note.note}</p>
                  </li>
                );
              })}
            </ul>
          )}

          {!loading && !loadError && pagination.totalPages > 1 && (
            <div className="adash-areanote__pagination">
              <button
                type="button"
                className="adash-areanote__pagination-btn"
                onClick={() => handlePageChange(pagination.pageNumber - 1)}
                disabled={!pagination.hasPreviousPage || loading}
                aria-label="Prethodna strana"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="adash-areanote__pagination-info">
                {pagination.pageNumber} / {pagination.totalPages}
              </span>
              <button
                type="button"
                className="adash-areanote__pagination-btn"
                onClick={() => handlePageChange(pagination.pageNumber + 1)}
                disabled={!pagination.hasNextPage || loading}
                aria-label="Sledeća strana"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </div>
      </Collapsible>

      {/* Notifikacija (toast) - npr. posle brisanja beleške */}
      {toast && (
        <div className="adash-areanotes__toast">
          <ToastNotification
            kind={toast.kind}
            title={toast.title}
            subtitle={toast.subtitle}
            lowContrast
            timeout={0}
            onClose={() => setToast(null)}
          />
        </div>
      )}

      {/* Potvrda brisanja beleške - modal (kao i za ostale entitete) */}
      <Theme theme={theme}>
        <Modal
          open={isDeleteModalOpen}
          onRequestClose={closeDeleteModal}
          modalHeading="Brisanje beleške smene"
          primaryButtonText={isDeleting ? 'Brisanje...' : 'Obriši'}
          secondaryButtonText="Otkaži"
          onRequestSubmit={() => handleDeleteNote(noteToDelete)}
          danger
          size="sm"
        >
          <p>Da li ste sigurni da želite da obrišete ovu belešku smene?</p>
          <p style={{ marginTop: '1rem', fontSize: '0.875rem', opacity: 0.7 }}>
            Ova akcija se ne može poništiti.
          </p>
        </Modal>
      </Theme>
    </div>
  );
};

export default AreaNotesPanel;
